import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { debounce } from 'lodash';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { AppView, Button, CardView, SettingsItemSwitch, Spacer } from 'design-system';
import Amount from 'design-system/Amount';
import AmountPill from 'design-system/AmountPill';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteAll, useEventBusEffect, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { editSavingsPlan, updateSavingRules } from 'features/pots/actions';
import AutosaveRulesWarningModal from 'features/pots/components/AutosaveRulesWarningModal';
import { AUTOSAVE_DEFAULT_MONTHLY_AMOUNT, AUTOSAVE_DEFAULT_WEEKLY_AMOUNT, AUTOSAVE_MONTHLY_INVESTMENT_AMOUNTS, AUTOSAVE_WEEKLY_INVESTMENT_AMOUNTS, busEvents, } from 'features/pots/constants';
import { selectAutosaveCollectionFrequency, selectAutosaveRulesArr } from 'features/pots/reducers/selectors';
import { SavingsPlanRuleType } from 'features/pots/types';
import createStyleSheets from 'utils/createStyleSheets';
const AutosaveCustomDepositSettings = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const [, , , fetch] = useFetchOld(muteAll);
    const mountRef = useRef(false);
    const store = useAppStore();
    const autosaveRulesArr = useAppSelector(selectAutosaveRulesArr);
    const savingsPlanId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const isPaused = useAppSelector((state) => state.pots.savingsPlan?.isPaused);
    const customAmount = useAppSelector((state) => state.pots.savingsPlan?.customAmount);
    const autosaveCollectionFrequency = useAppSelector(selectAutosaveCollectionFrequency);
    const amountOptions = autosaveCollectionFrequency === 'MONTHLY'
        ? AUTOSAVE_MONTHLY_INVESTMENT_AMOUNTS
        : AUTOSAVE_WEEKLY_INVESTMENT_AMOUNTS;
    const defaultAmount = autosaveCollectionFrequency === 'MONTHLY' ? AUTOSAVE_DEFAULT_MONTHLY_AMOUNT : AUTOSAVE_DEFAULT_WEEKLY_AMOUNT;
    const [selectedAmount, setSelectedAmount] = useState(isPaused ? 0 : customAmount?.value || 0);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_RULES}/>,
        });
    }, []);
    useEffect(() => {
        if (mountRef.current) {
            setSelectedAmount(customAmount?.value || 0);
        }
        else {
            mountRef.current = true;
        }
    }, [customAmount?.value]);
    const pauseAutosave = useCallback(() => {
        if (!savingsPlanId || isPaused)
            return;
        fetch(editSavingsPlan(savingsPlanId, { isPaused: true }));
    }, [isPaused, savingsPlanId]);
    useEventBusEffect(() => {
        setSelectedAmount(store.getState().pots.savingsPlan?.isPaused ? 0 : store.getState().pots.savingsPlan?.customAmount?.value || 0);
    }, [busEvents.UPDATE_SAVING_RULES_FAILURE_EVENT]);
    const onSwitchEvent = useMemo(() => debounce(async (value) => {
        if (savingsPlanId) {
            const newRules = (() => {
                if (isPaused) {
                    return [{ type: SavingsPlanRuleType.CUSTOM, amount: { value: defaultAmount, currency: 'GBP' } }];
                }
                return value
                    ? [
                        ...autosaveRulesArr,
                        { type: SavingsPlanRuleType.CUSTOM, amount: { value: defaultAmount, currency: 'GBP' } },
                    ]
                    : autosaveRulesArr.filter((rule) => rule.type !== SavingsPlanRuleType.CUSTOM);
            })();
            const oldSelectedAmount = selectedAmount;
            setSelectedAmount(value ? defaultAmount : 0);
            if (!newRules.length) {
                AutosaveRulesWarningModal.show(pauseAutosave, () => {
                    setSelectedAmount(oldSelectedAmount);
                });
            }
            else {
                dispatch(updateSavingRules({
                    savingsPlanId,
                    rules: newRules,
                }));
            }
        }
    }, 200), [autosaveRulesArr, defaultAmount, isPaused, pauseAutosave, savingsPlanId, selectedAmount]);
    const onChangeAmount = useCallback((amount) => async () => {
        if (savingsPlanId && amount !== selectedAmount) {
            setSelectedAmount(amount);
            const newRules = autosaveRulesArr.filter((rule) => rule.type !== SavingsPlanRuleType.CUSTOM);
            newRules.push({
                type: SavingsPlanRuleType.CUSTOM,
                amount: { value: amount, currency: 'GBP' },
            });
            dispatch(updateSavingRules({
                savingsPlanId,
                rules: newRules,
            }));
        }
    }, [autosaveRulesArr, savingsPlanId, selectedAmount]);
    const handleCustomDeposit = useCallback(() => {
        navigation.navigate('AutosaveChooseCustomAmount', {
            isMonthly: autosaveCollectionFrequency === 'MONTHLY',
            initialAmount: selectedAmount,
        });
    }, [autosaveCollectionFrequency, selectedAmount]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Custom deposit" subTitle={`Set up a custom amount to save every ${autosaveCollectionFrequency === 'MONTHLY' ? 'month' : 'week'}`}/>
          <CardView noMarginTop>
            <SettingsItemSwitch value={!!selectedAmount} title="Custom deposit" onValueChange={onSwitchEvent}/>
            {!!selectedAmount && (<View testID="main">
                <View style={styles.main}>
                  <View style={styles.amountContainer}>
                    <Text Text-16>Amount</Text>
                    <Amount value={{ value: selectedAmount, currency: 'GBP' }} Numbers-16 showFraction={false}/>
                  </View>
                  <Spacer.H16 />
                </View>
                <View style={styles.buttonContainer}>
                  {amountOptions.map((option) => (<AmountPill value={option} currency="GBP" key={String(option)} style={styles.roundBtn} onPress={onChangeAmount(option)} selected={selectedAmount === option}/>))}
                </View>
                <View style={styles.customAmountBtnWrapper}>
                  <Button flex square title="Enter custom amount" {...(amountOptions.includes(selectedAmount)
            ? { secondary: true, gray: true }
            : { brandLight: true })} onPress={handleCustomDeposit}/>
                </View>
              </View>)}
          </CardView>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    main: {
        paddingTop: rem(16),
        borderTopWidth: rem(1),
        paddingHorizontal: rem(16),
        borderTopColor: colors.background.dark,
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingHorizontal: rem(12),
    },
    roundBtn: {
        flex: 1,
        marginHorizontal: rem(4),
    },
    amountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: rem(8),
        justifyContent: 'space-between',
    },
    customAmountBtnWrapper: {
        paddingTop: rem(8),
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
}));
export default AutosaveCustomDepositSettings;
